import { useEffect } from 'react';
import { Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import useGeoData from '../../../store/useGeoData'; // Importa lo store completo
import { LayerStyleProperties } from '../../../store/types'; // Importa il tipo corretto da types.ts

/**
 * Gestisce l'evento 'click' sulla mappa MapLibre per visualizzare le proprietà
 * della feature cliccata in un pannello fisso (se il popup è abilitato per il layer).
 * @param map L'istanza della mappa MapLibre.
 */
export function useFeaturePopupOnClick(map: MaplibreMap | null) {

    useEffect(() => {
        if (!map) return;

        const handleClick = (e: MapMouseEvent & object) => {
            // Ottieni gli ID di tutti i layer GeoJSON gestiti dallo store
            const allGeoJsonLayerIds = useGeoData.getState().dataLayers.map(l => l.id);
            // Ottieni lo stato completo degli stili dei layer
            const layerStyles = useGeoData.getState().layerStyles;
            // Ottieni le azioni per il popup fisso dallo store
            const { showFixedPopup, hideFixedPopup } = useGeoData.getState();

            // Interroga le feature nel punto cliccato, solo sui nostri layer GeoJSON
            const features = map.queryRenderedFeatures(e.point, {
                layers: allGeoJsonLayerIds
            });

            // Se non ci sono feature nel punto cliccato, nascondi il popup fisso
            if (!features.length) {
                hideFixedPopup();
                return; // Nessuna feature dei nostri layer trovata
            }

            const feature = features[0]; // Prendi la feature più in alto
            const layerId = feature.layer.id; // Ottieni l'ID del layer della feature

            // Recupera lo stato dello stile per questo layer specifico
            const layerStyle: LayerStyleProperties | undefined = layerStyles[layerId];

            // Controlla se il popup è abilitato per questo layer
            if (!layerStyle || !layerStyle.isPopupEnabled) {
                 // Se il popup non è abilitato per questo layer, nascondi il popup fisso
                hideFixedPopup();
                return; // Popup non abilitato, non fare nulla
            }

            // Se il popup è abilitato, mostra le proprietà nel popup fisso
            // Passa le proprietà della feature, i campi popup configurati e l'ID del layer
            showFixedPopup(feature.properties ?? {}, layerStyle.popupFields ?? null, layerId); // Aggiunto layerId
        };

        map.on('click', handleClick);

        // Funzione di cleanup
        return () => {
            if (map && map.getStyle()) {
                try {
                    map.off('click', handleClick);
                } catch (e) {
                    // console.error("Errore durante la rimozione dell'handler 'click':", e);
                }
            } else {
                // console.warn("Mappa non disponibile o stile non caricato durante il cleanup di useFeaturePopupOnClick.");
            }
             // Nascondi il popup quando l'hook viene smontato o la mappa cambia
             useGeoData.getState().hideFixedPopup();
        };

    }, [map]); // Dipende solo dalla mappa
}